import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { AuthError, Session } from '@supabase/supabase-js';

interface UseSupabaseAuth {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
  session: Session | null;
  supabaseClient: typeof supabase;
}

export function useSupabaseAuth(): UseSupabaseAuth {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: fetchError } = await supabase.auth.getSession();
        if (fetchError) throw fetchError;
        
        setSession(session);
        setIsAuthenticated(!!session);
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError(err instanceof AuthError ? err : null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      
      setSession(session);
      setIsAuthenticated(!!session);
      setIsLoading(false);

      if (event === 'SIGNED_OUT') {
        // Clear any cached data
        localStorage.clear();
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (!session?.access_token) {
          console.error('Invalid session detected');
          setError(new AuthError('Invalid session'));
          localStorage.clear();
          return;
        }
        
        // For new sign-ins, check if we need to set the quiz status
        if (event === 'SIGNED_IN' && !localStorage.getItem('quizCompleted')) {
          localStorage.setItem('quizCompleted', 'false');
        }
      }
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { isAuthenticated, isLoading, error, session, supabaseClient: supabase };
}